/**
 * "Which lists are being used" as one button (user, 19/10/2026: "the lists
 * in use must be one button — clicking it expands; super intuitive and
 * smooth"): closed, it says how many lists and names the first two; open,
 * it slides down to every list the day sends to, in send order — its group,
 * the containers that send to it, drops, SMS, gateways and first → last send
 * (Danish first). The first 8 rows show; the rest on demand.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { ListChecks } from 'lucide-react';

import type { DayPlanPreview } from '../../../drop-generator.types';
import { instantDk } from '../../../utils/clock/dk-label';
import { ContainerChip } from '../../shared/ContainerChip';
import { GroupChip } from '../../shared/GroupChip';
import { Collapse } from '../section/Collapse';
import { SectionAction } from '../section/SectionAction';
import { SectionToggle } from '../section/SectionToggle';
import { listsInUse, type ListUse } from './lists-in-use';

const SHOWN = 8;
const GRID = 'grid grid-cols-[minmax(170px,1.4fr)_minmax(90px,0.8fr)_minmax(120px,1fr)_52px_72px_minmax(110px,1fr)_110px] items-center gap-3';

const shortRoute = (name: string) => name.replace(/^\([^)]*\)\s*/, '').replace(/\s*\([^)]*\)\s*$/, '').trim();
const span = (u: ListUse) => {
  const a = instantDk(u.firstAt).label;
  const b = instantDk(u.lastAt).label;
  return a === b ? `${a} 🇩🇰` : `${a} → ${b} 🇩🇰`;
};

const Row: FC<{ use: ListUse }> = ({ use }) => (
  <div className={`${GRID} border-b border-border/30 px-5 py-1.5 text-xs last:border-b-0`} data-testid="list-in-use">
    <span className="truncate font-mono font-medium" title={use.name}>{use.name}</span>
    <span className="flex min-w-0 flex-wrap gap-1">{use.groups.map((g) => <GroupChip key={g.name} name={g.name} color={g.color} />)}</span>
    <span className="flex min-w-0 flex-wrap gap-1">{use.containers.map((c) => <ContainerChip key={c.name} name={c.name} color={c.color} />)}</span>
    <span className="text-right font-mono tabular-nums">{use.drops}</span>
    <span className="text-right font-mono tabular-nums">{use.sms.toLocaleString('en-US')}</span>
    <span className="truncate text-muted-foreground" title={use.gateways.join(', ')}>{use.gateways.map(shortRoute).join(' · ') || '—'}</span>
    <span className="whitespace-nowrap text-right font-mono tabular-nums text-muted-foreground">{span(use)}</span>
  </div>
);

/** "AU_ACE_REG+FTD_LAST30, AU_ACE_REG+FTD_LAST7 +19" — what the closed button promises. */
const preview2 = (uses: ListUse[]) => [uses.slice(0, 2).map((u) => u.name).join(', '), uses.length > 2 ? `+${uses.length - 2}` : null].filter(Boolean).join(' ');

export const ListsInUse: FC<{ preview: DayPlanPreview; open: boolean; onToggle: () => void }> = ({ preview, open, onToggle }) => {
  const [all, setAll] = useState(false);
  const uses = listsInUse(preview);
  if (uses.length === 0) return null;
  return (
    <div className="border-b border-border/40" data-testid="lists-in-use">
      <SectionToggle icon={ListChecks} title="Lists in use today" badge={uses.length} summary={preview2(uses)} open={open} onToggle={onToggle} />
      <Collapse open={open}>
        <div className="overflow-x-auto">
          <div className="min-w-[880px]">
            <div className={`${GRID} px-5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}>
              <span>List</span><span>Group</span><span>Containers</span><span className="text-right">Drops</span><span className="text-right">SMS</span><span>Gateways</span><span className="text-right">Sends</span>
            </div>
            {(all ? uses : uses.slice(0, SHOWN)).map((u) => <Row key={u.key} use={u} />)}
          </div>
        </div>
        {uses.length > SHOWN ? (
          <div className="px-5 py-2">
            <SectionAction quiet onClick={() => setAll((v) => !v)}>
              {all ? 'Show the first 8' : `Show all ${uses.length} lists`}
            </SectionAction>
          </div>
        ) : null}
      </Collapse>
    </div>
  );
};
